import React, { useContext, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../contexts/AuthContext';
import AuthService from '../services/AuthService';
import Roles from '../config/roles';
import logo from '../images/CRM-Logo-large.png';

const POLL_INTERVAL = 2500;
const MAX_ATTEMPTS = 40; // ~100s before giving up

const messages = [
    'Setting up your workspace…',
    'Activating your subscription…',
    'Configuring your account…',
    'Almost there…',
    'Just a moment…',
];

const SubscriptionSuccessPage = () => {
    const navigate = useNavigate();
    const { role, setHasActiveSubscription } = useContext(AuthContext);
    const [msgIndex, setMsgIndex] = useState(0);
    const [fade, setFade] = useState(true);
    const attemptsRef = useRef(0);
    const intervalRef = useRef(null);

    useEffect(() => {
        const msgTimer = setInterval(() => {
            setFade(false);
            setTimeout(() => {
                setMsgIndex(i => (i + 1) % messages.length);
                setFade(true);
            }, 400);
        }, 3000);

        return () => clearInterval(msgTimer);
    }, []);

    useEffect(() => {
        intervalRef.current = setInterval(async () => {
            attemptsRef.current += 1;
            try {
                const account = await AuthService.account();
                if (account.hasActiveSubscription) {
                    clearInterval(intervalRef.current);
                    localStorage.setItem('HAS_SUBSCRIPTION', 'true');
                    setHasActiveSubscription(true);
                    sessionStorage.setItem('SHOW_WELCOME', account.firstName || account.username || '');
                    navigate(role === Roles.USER ? '/projects' : '/home', { replace: true });
                }
            } catch {
                // keep polling
            }
            if (attemptsRef.current >= MAX_ATTEMPTS) {
                clearInterval(intervalRef.current);
                navigate('/manage-subscription', { replace: true });
            }
        }, POLL_INTERVAL);

        return () => clearInterval(intervalRef.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: '#f0f4f7',
                gap: 5,
            }}
        >
            <img src={logo} alt="Plankton CRM" style={{ width: '200px', height: 'auto' }} />

            {/* Animated ring spinner */}
            <Box sx={{ position: 'relative', width: 80, height: 80 }}>
                <Box sx={{
                    position: 'absolute', inset: 0,
                    borderRadius: '50%',
                    border: '4px solid #e0e0e0',
                }} />
                <Box sx={{
                    position: 'absolute', inset: 0,
                    borderRadius: '50%',
                    border: '4px solid transparent',
                    borderTopColor: '#427898',
                    animation: 'spin 1s linear infinite',
                }} />
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </Box>

            {/* Animated message */}
            <Box sx={{ textAlign: 'center', minHeight: 60 }}>
                <Typography
                    variant="h6"
                    fontWeight={600}
                    sx={{
                        color: '#427898',
                        opacity: fade ? 1 : 0,
                        transition: 'opacity 0.4s ease',
                        mb: 1,
                    }}
                >
                    {messages[msgIndex]}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                    This usually takes just a few seconds.
                </Typography>
            </Box>

            {/* Pulsing dots */}
            <Box sx={{ display: 'flex', gap: 1 }}>
                {[0, 1, 2].map(i => (
                    <Box
                        key={i}
                        sx={{
                            width: 10, height: 10,
                            borderRadius: '50%',
                            backgroundColor: '#427898',
                            animation: `pulse 1.4s ease-in-out ${i * 0.2}s infinite`,
                        }}
                    />
                ))}
                <style>{`
                    @keyframes pulse {
                        0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
                        40% { transform: scale(1); opacity: 1; }
                    }
                `}</style>
            </Box>
        </Box>
    );
};

export default SubscriptionSuccessPage;
